import { type Institution } from '../../services/institutions';
import { type ProviderUser, type Purchase } from '../../services/users';
import { type NodeRoomV2 } from '../../services/room';
import SegmentTracker from './trackers/segmentTracker';
import UserpilotTracker from './trackers/userpilotTracker';
import { UserPlanType } from '../../hooks/useUserSubscription/useUserSubscription.types';

export type Properties = Record<string, unknown>;

export type SignUpPlanType = 'free' | 'pro' | 'premium';

/** Stripe-backed fields from `getBillingData`, sent as analytics identify traits. */
export type IdentifyBillingData = {
  stripeCustomerId: string | null;
  stripeSubscriptionStatus: string | null;
  isOnTrial: boolean;
};

export type Tracker = {
  id: string;
  identify: (
    user?: ProviderUser,
    institution?: Institution,
    latestPurchase?: Purchase,
    rooms?: NodeRoomV2[],
    currentPlan?: UserPlanType,
    userRole?: string,
    authId?: string,
    tenantId?: string,
    billing?: IdentifyBillingData,
  ) => void | Promise<void>;
  track: (event: TrackedEvents, properties?: Properties) => Promise<void>;
  reset?: () => void;
};

export const TRACKERS: () => Tracker[] = () => [
  SegmentTracker,
  UserpilotTracker,
];

// Event values should be agreed with #intelligence-team as they will be tracked in Segment
export enum TrackedEvents {
  CaptionsToggled = 'Captions Toggled',
  /* Sessions Events */
  AppLaunched = 'App Launched',
  BackgroundEffectApplied = 'Background Effect Applied',
  CallHoldLaunched = 'Call Hold Launched',
  CallHoldResumed = 'Call Hold Resumed',
  CallRated = 'Call Rated',
  FullscreenToggled = 'Fullscreen Toggled',
  LiveChatMessageSent = 'Live Chat Message Sent',
  ParticipantsAddedToCall = 'Participants Added To Call',
  PictureInPictureToggled = 'Picture In Picture Toggled',
  ScreenshareEnded = 'Screenshare Ended',
  ScreenshareInitiated = 'Screenshare Initiated',
  ScreenshareStarted = 'Screenshare Started',
  ScreenshareZoomChanged = 'Screenshare Zoom Changed',
  ScreenshareRequestCancelled = 'Screenshare Request Cancelled',
  ScreenshareRequestRejected = 'Screenshare Request Rejected',
  ScreenshareRequested = 'Screenshare Requested',
  EndCall = 'Session Closed',
  StartCall = 'Session Started',
  TestPatientAdded = 'Test Patient Added',
  EndTestCall = 'Test Session Closed',
  StartTestCall = 'Test Session Started',
  PrecallTestButtonClicked = 'Precall test button clicked',
  TroubleshootingInitiated = 'Troubleshooting Initiated',
  MediaAccessRequested = 'Media Access Requested',
  CheckInDeviceAccessTroubleshootingInitiated = 'Checkin Device Access Troubleshooting Initiated',
  CheckInStarted = 'Checkin Started',
  CheckInCompleted = 'Checkin Completed',
  CheckInModalLoaded = 'Checkin Modal Loaded',
  CameraTested = 'Camera Tested',
  MicrophoneTested = 'Microphone Tested',
  SpeakerTested = 'Speaker Tested',
  NameEntered = 'Name Entered',
  RoomEntered = 'Room Entered',
  ButtonClicked = 'Button Clicked',
  PatientInvited = 'Patient Invited',
  PatientExternalEmailInviteOpened = 'Patient External Email Invite Opened',

  /** When the file(s) upload begins. */
  FileTransferStarted = 'File Transfer Started',

  /** When the file(s) upload is completed (sender side). */
  FileTransferFinished = 'File Transfer Finished',

  /** When all of the files have been downloaded by the receiver. */
  FileTransferAccepted = 'File Transfer Accepted',

  /* Photo Capture */
  PhotoCaptureStarted = 'Photo Capture Started',
  PhotoCaptureTaken = 'Photo Capture Taken',
  PhotoCaptureRetaken = 'Photo Capture Retaken',
  PhotoCaptureDownloaded = 'Photo Capture Downloaded',
  PhotoCaptureCanceled = 'Photo Capture Canceled', // properties: `{ canceled_by: 'provider' | 'patient' }`

  AvatarUploaded = 'Avatar Uploaded',

  /** Role Selection Events */
  ProviderOptionClicked = 'Provider Option Clicked',
  PatientOptionClicked = 'Patient Option Clicked',
  ProviderSelectionContinueClicked = 'Provider Selection Continue Clicked',
  PatientSelectionContinueClicked = 'Patient Selection Continue Clicked',

  /**
   * Consolidated upgrade funnel — prefer these for new code.
   * `location` uses *_modal suffix (plan_overview_modal, payment_summary_modal, …).
   * Legacy granular members below stay until all call sites migrate (then remove in a follow-up MR).
   */
  UpgradeInitiated = 'Upgrade Initiated', // location, optional planSelected
  UpgradeAbandoned = 'Upgrade Abandoned', // locations: plan_overview_modal, payment_summary_modal, clinic_checkout_modal, seat_selection_modal
  SubscriptionCycleChanged = 'Subscription Cycle Changed', // locations: plan_overview_modal, payment_summary_modal
  UpgradePlanSelected = 'Upgrade Plan Selected', // locations: plan_overview_modal, payment_summary_modal; plan_type: day_pass | premium | clinic
  StripeCheckoutInitiated = 'Stripe Checkout Initiated', // plan_type, seat_count (hosted Checkout redirect)
  StripeCheckoutCanceled = 'Stripe Checkout Canceled', // location: plan_overview_modal (upgrade modal); signup uses dashboard return
  UpgradeCompleted = 'Upgrade Completed', // plan_type: day_pass | premium | clinic

  /** Consolidated clinic checkout helpers (promo + recaptcha). */
  UpgradePromoCodeAdded = 'Upgrade Promo Code Added',
  UpgradePromoCodeRejected = 'Upgrade Promo Code Rejected',
  UpgradeRecaptchaCompleted = 'Upgrade Recaptcha Completed',
  UpgradeRecaptchaFailed = 'Upgrade Recaptcha Failed',

  /**
   * @deprecated Prefer consolidated upgrade events above; remove once nothing references these.
   * Pre-consolidation granular upgrade / clinic instrumentation (historical event names).
   */
  UpgradeAbandonedDuringPlanOverviewPage = 'Upgrade Abandoned During Plan Overview Page',
  UpgradeAbandonedDuringPaymentSummaryPage = 'Upgrade Abandoned During Payment Summary Page',
  SubscriptionCycleChangedDuringPlanOverviewPage = 'Subscription Cycle Changed During Plan Overview Page',
  SubscriptionCycleChangedDuringPaymentSummaryPage = 'Subscription Cycle Changed During Payment Summary Page',
  DayPassTooltipViewedOnUpgradePlanOverviewPage = 'Day Pass Tooltip Viewed On Upgrade Plan Overview Page',
  DayPassPlanSelected = 'Day Pass Plan Selected',
  UpgradePlanSelectedToMoveToPaymentSummaryPage = 'Upgrade Plan Selected To Move To Payment Summary Page',
  BackButtonClickedOnPaymentSummaryPage = 'Back Button Clicked on Payment Summary Page',
  UpgradePromoCodeAddedSuccessfully = 'Upgrade Promo Code Added Successfully',
  UpgradePromoCodeSelectedInvalid = 'Invalid Upgrade Promo Code Selected',
  UpgradePromoCodeRemoved = 'Upgrade Promo Code Removed',
  UpgradeRecaptchaCompletedSuccessfully = 'Upgrade Recaptcha Completed Successfully',
  SubscribeButtonClickedToMoveToConfirmationPage = 'Subscribe Button Clicked To Move To Confirmation Page',
  StripeSourceAddedSuccessfullyDuringUpgrade = 'Stripe Source Added Successfully During Upgrade',
  StripeSourceFailedToAddDuringUpgrade = 'Stripe Source Failed to Add During Upgrade',
  UpgradePaymentInitiatedSuccessfully = 'Upgrade Payment Initiated Successfully',
  UpgradePaymentFailedToInitiate = 'Upgrade Payment Failed To Initiate',
  UpgradePaymentSuccessfullyConfirmed = 'Upgrade Payment Successfully Confirmed',
  UpgradePaymentFailedToConfirm = 'Upgrade Payment Failed to Confirm',
  UpgradePaymentCompletedSuccessfully = 'Upgrade Payment Completed Successfully',
  UpgradePaymentFailedToComplete = 'Upgrade Payment Failed to Complete',
  UpgradeFlowCompleted = 'Upgrade Flow Completed',
  ClinicSetupInvoked = 'Clinic Setup Invoked',
  ClinicSetupComplete = 'Clinic Setup Complete',
  ClinicSetupClosed = 'Clinic Setup Closed',
  BaaViewed = 'BAA Viewed',
  BaaDownloaded = 'BAA Downloaded',
  BrandingInvoked = 'Branding Invoked',
  BrandingClosed = 'Branding Closed',
  BrandingPublished = 'Branding Published',
  SeatsTooltipOnUpgradePaymentSummaryPageViewed = 'Seats Tooltip On Upgrade Payment Summary Page Viewed',
  SetupFeeTooltipOnUpgradePaymentSummaryPageViewed = 'Setup Fee Tooltip On Upgrade Payment Summary Page Viewed',
  SalesTaxTooltipOnUpgradePaymentSummaryPageViewed = 'Sales Tax Tooltip On Upgrade Payment Summary Page Viewed',

  // apps
  AppsPageVisited = 'Apps Page Visited',
  AppCardOpened = 'App Card Opened',
  AppInstallStarted = 'App Install Started',
  AppInstalled = 'App Installed',
  AppRemovalStarted = 'App Removal Started',
  AppRemoved = 'App Removed',
  AppInformationOpened = 'App Information Opened',
  AppSettingsOpened = 'App Settings Opened',
  PrototypesPageVisited = 'Prototypes Page Visited',
  AppLibrarySortChanged = 'App Library Sort Changed',
  AccessPrototypesModalOpened = 'Access Prototypes Modal Opened',
  AccessPrototypesModalClosed = 'Access Prototypes Modal Closed',
  AccessPrototypesOptInCompleted = 'Access Prototypes Opt-In Completed',

  /** Custom Waiting Room */
  WaitingRoomEditorOpened = 'Waiting Room Editor Opened',
  WaitingRoomUpdated = 'Waiting Room Updated',
  WaitingRoomPreviewed = 'Waiting Room Previewed',

  // Performance
  HighCpuDetected = 'High CPU Detected',

  EmailEnteredAtSignUp = 'Email Entered At Sign Up',
  SignUpOnboardingComplete = 'Sign Up Onboarding Complete',
  SignUpPaymentComplete = 'Sign Up Payment Complete',
  SignIn = 'Sign In',

  WhatsNewClicked = "What's New Clicked",

  /** Dashboard patient queue */
  QueueFiltered = 'Queue Filtered',
  QueueSorted = 'Queue Sorted',

  /** Patient Transfer */
  PatientTransferInitiated = 'Patient Transfer Initiated',
  PatientTransferDismissed = 'Patient Transfer Dismissed',
  PatientTransferStarted = 'Patient Transfer Started',
  PatientTransferCompleted = 'Patient Transfer Completed',
  PatientTransferFailed = 'Patient Transfer Failed',
  PatientTransferCanceled = 'Patient Transfer Canceled',
}

export const identify = async (
  user?: ProviderUser,
  institution?: Institution,
  latestPurchase?: Purchase,
  rooms?: NodeRoomV2[],
  currentPlan?: UserPlanType,
  userRole?: string,
  authId?: string,
  tenantId?: string,
  billing?: IdentifyBillingData,
): Promise<void> => {
  await Promise.all(
    TRACKERS().map((tracker) =>
      tracker.identify(
        user,
        institution,
        latestPurchase,
        rooms,
        currentPlan,
        userRole,
        authId,
        tenantId,
        billing,
      ),
    ),
  );
};

export const track = async (
  event: TrackedEvents,
  properties?: Properties,
): Promise<void> => {
  await Promise.all(
    TRACKERS().map(async (tracker) => tracker.track(event, properties)),
  );
};

export const getAnonymousId = (): string | undefined => {
  const { analytics } = window;
  return analytics?.user?.() ? analytics.user().anonymousId() : undefined;
};

export const reset = (): void => {
  TRACKERS().forEach((tracker) => {
    if (tracker.reset) {
      tracker.reset();
    }
  });
  void import('../../hooks/usePostHog').then((m) => m.resetPostHog());
};
